import { Checkbox, Divider } from "@mui/material";
import {
  FormikErrors,
  FormikHandlers,
  FormikHelpers,
  FormikTouched,
} from "formik";
import FieldRow from "@/shared/components/FieldRow";
import CharacterLimitTextField from "@/shared/components/CharacterLimitTextField";
import ImageDropZone from "@/shared/components/ImageDropZone";
import useObjectUrl from "@/shared/hooks/useObjectUrl";
import {
  HOME_IMAGE_CONSTRAINTS,
  HOME_SUBTITLE_MAX_LENGTH,
  HOME_TITLE_MAX_LENGTH,
  SAVED_HOME_IMAGE_LABEL,
} from "../constants/promotional-banner";
import { PromotionalBannerFormValues } from "../types/organization-form";

interface PromotionalBannerFieldsProps {
  values: PromotionalBannerFormValues;
  errors: FormikErrors<PromotionalBannerFormValues>;
  touched: FormikTouched<PromotionalBannerFormValues>;
  handleChange: FormikHandlers["handleChange"];
  handleBlur: FormikHandlers["handleBlur"];
  setFieldValue: (
    field: keyof PromotionalBannerFormValues,
    value: PromotionalBannerFormValues[keyof PromotionalBannerFormValues],
  ) => void;
  setFieldTouched: FormikHelpers<PromotionalBannerFormValues>["setFieldTouched"];
  /** Data URL of the image already saved for the organization (Edit). */
  savedImageUrl?: string | null;
  /** Image error returned by the backend on the last save attempt. */
  uploadError?: string | null;
  onUploadErrorDismiss?: () => void;
}

// "Show promotional banner" section of the Add/Edit Organization forms
// (ESR-1655): the customer app home screen banner headline, subline and
// optional image. Only rendered on the super-admin-only organization pages.
const PromotionalBannerFields = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  savedImageUrl,
  uploadError,
  onUploadErrorDismiss,
}: PromotionalBannerFieldsProps) => {
  const newImageUrl = useObjectUrl(values.homeImageFile);
  const previewUrl =
    newImageUrl ?? (values.removeHomeImage ? null : (savedImageUrl ?? null));

  return (
    <>
      <Divider sx={{ my: "32px" }} />

      <FieldRow
        label="Show promotional banner"
        description="Banner appears on the customer app home screen"
        align="center"
      >
        <Checkbox
          id="showPromotionalBanner"
          checked={values.showPromotionalBanner}
          onChange={(event) =>
            setFieldValue("showPromotionalBanner", event.target.checked)
          }
          data-testid="organization-show-promotional-banner"
        />
      </FieldRow>

      {values.showPromotionalBanner && (
        <>
          <FieldRow label="Headline" required>
            <CharacterLimitTextField
              id="homeTitle"
              value={values.homeTitle}
              maxLength={HOME_TITLE_MAX_LENGTH}
              onChange={(event) => {
                handleChange(event);
                // Save is disabled while the form is invalid, so submit never
                // touches the headline. Mark it on typing so clearing it shows
                // "Headline is required" right away, not on opening the section.
                if (!touched.homeTitle)
                  setFieldTouched("homeTitle", true, false);
              }}
              onBlur={handleBlur}
              error={touched.homeTitle && !!errors.homeTitle}
              errorMessage={touched.homeTitle && errors.homeTitle}
              fullWidth
            />
          </FieldRow>
          <FieldRow label="Subline">
            <CharacterLimitTextField
              id="homeSubtitle"
              value={values.homeSubtitle}
              maxLength={HOME_SUBTITLE_MAX_LENGTH}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.homeSubtitle && !!errors.homeSubtitle}
              errorMessage={touched.homeSubtitle && errors.homeSubtitle}
              fullWidth
            />
          </FieldRow>
          <FieldRow
            label="Image"
            description="Adds a photo above the banner text. Without one, the text sits on a solid brand-color card."
          >
            <ImageDropZone
              constraints={HOME_IMAGE_CONSTRAINTS}
              hint={
                <>
                  Cropped to 3:1 · min {HOME_IMAGE_CONSTRAINTS.minWidth} ×{" "}
                  {HOME_IMAGE_CONSTRAINTS.minHeight} px · up to{" "}
                  {HOME_IMAGE_CONSTRAINTS.maxSizeMb} MB
                  <br />
                  {HOME_IMAGE_CONSTRAINTS.formatLabel}
                </>
              }
              previewUrl={previewUrl}
              fileName={values.homeImageFile?.name ?? SAVED_HOME_IMAGE_LABEL}
              onFileSelect={(file) => setFieldValue("homeImageFile", file)}
              onRemove={() => {
                setFieldValue("homeImageFile", null);
                // Only a saved image needs an explicit delete on the backend.
                setFieldValue("removeHomeImage", Boolean(savedImageUrl));
              }}
              error={uploadError}
              onErrorDismiss={onUploadErrorDismiss}
            />
          </FieldRow>
        </>
      )}
    </>
  );
};

export default PromotionalBannerFields;
